import { Collapse, Dialog, useMediaQuery, useTheme } from "@mui/material";
import { styled } from "@mui/system";
import React from "react";
import ContainedButton from "src/components/common/ContainedButton";
import ReferLegalDialog from "src/components/legal/ReferLegalDialog";
import CrossIcon from "src/icons/CrossIcon";
import DownArrowIcon from "src/icons/portfolio/DownArrowIcon";
import { useUserSlice } from "src/store/features/user.slice";
import InfoBlock from "../ReferralInfo/InfoBlock";
import GenericTextBlock from "../extra/GenericTextBlock";
import QRCodeBadge from "../extra/QRCodeBadge";
import useRafCalculations from "../extra/useRafCalculations";
import InviteHeaderModal from "./InviteHeaderModal";
import { trackEvent } from "src/Amplitude";
import useVerificationNavigation from "src/components/common/useVerificationNavigation";

export const DialogWrapper = styled(Dialog)(() => ({
  display: "flex",
  width: "100%",
  flexDirection: "column",
  alignItems: "center",
  borderRadius: "10px",
  background: "#FFF",

  "& .MuiDialog-container": {
    width: "100%",
  },
  // because we use Mui Dialog component, we need to style the paper component
  "& .MuiDialog-paper": {
    /* Style scrollbar for WebKit (Chrome, Safari) */
    "&::-webkit-scrollbar": {
      width: "10px" /* Width of the scrollbar */,
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#E5E7E8" /* Color of the scrollbar thumb */,
      borderRadius: "5px" /* Border radius of the scrollbar thumb */,
    },

    "&::-webkit-scrollbar-track": {
      backgroundColor: "transparent" /* Color of the scrollbar thumb */,
    },
  },
}));

export const HeaderWrapper = styled("div")(() => ({
  display: "flex",
  padding: "16px 20px",
  justifyContent: "center",
  alignItems: "center",
  gap: "4px",
  alignSelf: "stretch",
  background: "#002035",
}));

export const HeaderTitle = styled("div")(() => ({
  flex: "1 0 0",
  color: "#FFF",
  textAlign: "center",
  fontFamily: "Roobert",
  fontSize: "23px",
  fontStyle: "normal",
  fontWeight: 700,
  lineHeight: "28px",
  letterSpacing: "-0.46px",
}));

export const HeaderIcon = styled("div")(() => ({
  display: "flex",
  width: "16px",
  height: "16px",
  justifyContent: "center",
  alignItems: "center",
  cursor: "pointer",
}));

export const ContentWrapper = styled("div")(() => ({
  display: "flex",
  padding: "24px 28px",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "28px",
  alignSelf: "stretch",
}));

export const TopWrapper = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  gap: "16px",
  alignSelf: "stretch",
}));

interface CollapseListProps {
  hide: boolean;
}
const CollapseList = styled("div")<CollapseListProps>(({ hide }) => ({
  display: hide ? "none" : "flex",
  flexDirection: "column",
  width: "100%",
}));

const CollapseTitle = styled("div")(() => ({
  display: "flex",
  alignItems: "center",
  gap: "12px",
  alignSelf: "stretch",
  borderRadius: "4px",
  cursor: "pointer",

  // hover styles for childs
  "&:hover > div": {
    color: "#4E7086",
  },
}));

const HeaderText = styled("div")(() => ({
  flex: "1 0 0",
  color: "#002035",
  fontFamily: "Söhne",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "17px",
}));

interface ArrowWrapperProps {
  clicked: boolean;
}
const ArrowWrapper = styled("div")<ArrowWrapperProps>(({ clicked }) => ({
  animation: clicked ? "downwards 0.5s forwards" : "leftwards 0.5s forwards",

  // ANIMATION
  "@keyframes leftwards": {
    " 0%": { transform: "rotate(0deg);" },
    "100% ": { transform: "rotate(-90deg)" },
  },

  "@keyframes downwards": {
    " 0%": { transform: "rotate(-90deg);" },
    "100% ": { transform: "rotate(0deg)" },
  },
}));

const ButtonWrapper = styled("div")(() => ({
  width: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  paddingTop: "12px",
  position: "relative",
  top: 0,
  right: 0,
}));

interface TooltipProps {
  show: boolean;
}
const Tooltip = styled("div")<TooltipProps>(({ show }) => ({
  padding: "12px 16px",
  display: "inline-flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "12px",
  borderRadius: "10px",
  background: "rgba(0, 32, 53, 0.80)",
  color: "#FFF",
  fontFamily: "Söhne",
  fontSize: "12px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "14px",
  position: "absolute",
  bottom: 80,
  zIndex: 1,

  // smooth display transition
  transition: "opacity 0.2s linear, visibility 1s ease",
  opacity: show ? 1 : 0,
  visibility: show ? "visible" : "hidden",
}));

const TierWrapperTitle = styled("div")(() => ({
  display: "flex",
  alignItems: "center",
  gap: "5px",
  height: "17px",
  alignSelf: "stretch",
  color: "#002035",
  fontFamily: "Söhne",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "17px",
}));

const TermsLink = styled("span")(() => ({
  color: "#FC4C4C",
  fontFamily: "Söhne",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: 400,
  lineHeight: "17px",
  textDecorationLine: "underline",
  cursor: "pointer",
}));

interface RaFInviteModalProps {
  open: boolean;
  fullScreen?: boolean;
  onClose: () => void;
}
const RaFInviteModal = (props: RaFInviteModalProps) => {
  const { open, fullScreen, onClose } = props;
  const {
    raf: { tier, referredAmount, rafCode, OLD_RAF_SYSTEM },
    details: { customerId, name },
    isUnverified,
    isPendingVerification,
  } = useUserSlice();
  const { handleVerification } = useVerificationNavigation();

  // used to control size of the QR code on XS mobile devices
  const xsMobile = useMediaQuery(useTheme().breakpoints.down(400));

  const [checked, setChecked] = React.useState(false);

  const handleChange = () => {
    setChecked((prev) => !prev);
  };

  // get common RaF calculations, string, text etc..
  const { textBlockHeader, referredInfoText } = useRafCalculations(tier, referredAmount);

  const [clicked, setClicked] = React.useState<boolean>(false);
  const handleOnClick = () => {
    // end function if false
    if (!window.isSecureContext) {
      return;
    }

    navigator.clipboard.writeText(`app.calebandbrown.com/signup?referrer=${rafCode}`);
    setClicked(true);
    trackEvent("Clicked share code button", { component: "RaF Invite Modal" }, "RaF");
    setTimeout(() => {
      setClicked(false);
    }, 1000);
  };

  // TERMS OF RaF CODE
  const [openReferLegal, setOpenReferLegal] = React.useState<boolean>(false);
  // eslint-disable-next-line
  const [fetchUrlError, setFetchUrlError] = React.useState<boolean>(false);
  const handleOpenTermsOfRaF = () => {
    setOpenReferLegal(true);
  };
  const handleCloseReferLegal = () => {
    setOpenReferLegal(false);
  };

  return (
    <DialogWrapper
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      sx={{ background: "rgba(0, 32, 53, 0.7)" }}>
      {/* Modal header */}
      <HeaderWrapper>
        <HeaderTitle>Refer a Friend</HeaderTitle>
        <HeaderIcon onClick={onClose}>
          <CrossIcon color="#FFF" />
        </HeaderIcon>
      </HeaderWrapper>
      <ContentWrapper>
        <TopWrapper>
          {/* User info banner */}
          <InviteHeaderModal name={name} customerID={customerId} tier={tier} />

          {/* Highlight text block */}
          {(!OLD_RAF_SYSTEM || !isUnverified) && (
            <GenericTextBlock
              header={textBlockHeader}
              text="Trading fees from your referred friends"
            />
          )}

          {/* Collapse info */}
          <CollapseList hide={tier === "black" || OLD_RAF_SYSTEM}>
            <CollapseTitle onClick={handleChange}>
              <HeaderText>Upgrade to the next tier</HeaderText>
              <ArrowWrapper clicked={checked}>
                <DownArrowIcon />
              </ArrowWrapper>
            </CollapseTitle>
            <Collapse in={checked} orientation="vertical" sx={{ width: "100%" }}>
              <InfoBlock
                title="You referred"
                highlightText={referredAmount.toString()}
                boldText="trading clients"
                infoText={referredInfoText}
              />
            </Collapse>
          </CollapseList>

          {/* QR code */}
          <QRCodeBadge tier={tier} backgroundSize={xsMobile ? "310px" : "330px"} qrSize={280} />

          <TierWrapperTitle>
            Learn about our <TermsLink onClick={handleOpenTermsOfRaF}> terms here</TermsLink>
          </TierWrapperTitle>
          {/* Share Button */}
          <ButtonWrapper>
            {isUnverified || isPendingVerification ? (
              <ContainedButton
                color="salmon"
                width="100%"
                height="46px"
                onClick={handleVerification}>
                Verify Account
              </ContainedButton>
            ) : (
              <>
                <Tooltip show={clicked}>Code copied to clipboard!</Tooltip>
                <ContainedButton color="salmon" width="100%" height="46px" onClick={handleOnClick}>
                  Share Invite
                </ContainedButton>
              </>
            )}
          </ButtonWrapper>
        </TopWrapper>
      </ContentWrapper>
      <ReferLegalDialog
        refetchUserQuery={() => setOpenReferLegal(false)}
        optIn={openReferLegal}
        setOptIn={setOpenReferLegal}
        setFetchUrlError={setFetchUrlError}
        closeButton
        onClose={handleCloseReferLegal}
      />
    </DialogWrapper>
  );
};

export default RaFInviteModal;
